import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  Box, Typography, Paper, Tooltip, LinearProgress, Chip, Button,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FormattedCurrency from './FormattedCurrency'

const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  let startDow = firstDay.getDay() - 1
  if (startDow < 0) startDow = 6
  const totalDays = lastDay.getDate()
  return { startDow, totalDays }
}

function formatDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function parseDate(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return { year: y, month: m - 1, day: d }
}

function getFourMonths(baseDate) {
  const { year, month } = parseDate(baseDate)
  const months = []
  let y = year, m = month
  for (let i = 0; i < 4; i++) {
    months.push({ year: y, month: m })
    m++
    if (m > 11) { m = 0; y++ }
  }
  return months
}

export default function TimeLapseCalendar({
  tenantSlug, propertyId, simulatedDate, onComplete,
}) {
  const today = new Date().toISOString().split('T')[0]
  const startDate = simulatedDate || today
  const [currentDate, setCurrentDate] = useState(startDate)
  const [targetDate, setTargetDate] = useState(null)
  const [processedDates, setProcessedDates] = useState(() => new Set())
  const [events, setEvents] = useState([])
  const [isRunning, setIsRunning] = useState(false)
  const [isAdvancing, setIsAdvancing] = useState(false)
  const intervalRef = useRef(null)
  const abortRef = useRef(false)

  const reachedTarget = targetDate && currentDate >= targetDate

  const advanceOneDay = useCallback(async () => {
    if (isAdvancing) return false
    setIsAdvancing(true)
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(
        `/m/${tenantSlug}/properties/${propertyId}/advance_time_lapse`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
        }
      )
      if (!resp.ok) return false
      const result = await resp.json()
      setCurrentDate(result.date)
      setProcessedDates(prev => new Set([...prev, result.date]))
      if (result.events && result.events.length > 0) {
        setEvents(prev => [...prev, ...result.events.map(e => ({ ...e, date: result.date }))])
      }
      return result.date < targetDate
    } catch {
      return false
    } finally {
      setIsAdvancing(false)
    }
  }, [tenantSlug, propertyId, targetDate, isAdvancing])

  const handleStart = useCallback(async () => {
    if (!targetDate) return
    setIsRunning(true)
    abortRef.current = false
    const step = async () => {
      if (abortRef.current) return
      const canContinue = await advanceOneDay()
      if (canContinue && !abortRef.current) {
        intervalRef.current = setTimeout(step, 300)
      } else {
        setIsRunning(false)
        if (!abortRef.current) onComplete?.()
      }
    }
    step()
  }, [advanceOneDay, onComplete, targetDate])

  useEffect(() => {
    return () => {
      abortRef.current = true
      if (intervalRef.current) clearTimeout(intervalRef.current)
    }
  }, [])

  // Progress
  const totalDays = targetDate ? (() => {
    const s = new Date(...Object.values(parseDate(startDate)))
    const t = new Date(...Object.values(parseDate(targetDate)))
    return Math.max(1, Math.round((t - s) / 86400000))
  })() : 0
  const doneCount = processedDates.size
  const progress = totalDays > 0 ? Math.min(100, (doneCount / totalDays) * 100) : 0

  const months = getFourMonths(startDate)

  const handleDayClick = (dateStr) => {
    if (isRunning || reachedTarget) return
    if (dateStr <= startDate) return
    setTargetDate(dateStr)
  }

  return (
    <Box data-testid="time-lapse-calendar">
      {/* Status bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip
          icon={<AccessTimeIcon />}
          label={currentDate}
          color="warning"
          size="small"
          data-testid="time-lapse-current-date"
        />
        {targetDate && !reachedTarget && !isRunning && (
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={handleStart}
            disabled={isAdvancing}
            data-testid="time-lapse-start"
          >
            Starten bis {targetDate}
          </Button>
        )}
        {isRunning && (
          <Chip label="Läuft..." color="info" size="small" data-testid="time-lapse-running" />
        )}
        {reachedTarget && (
          <Chip label="Ziel erreicht" color="success" size="small" data-testid="time-lapse-done" />
        )}
      </Box>

      {/* Progress bar — only shown when target is selected */}
      {targetDate && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 1 }}
            data-testid="time-lapse-progress"
          />
          <Typography variant="caption" color="text.secondary">
            {doneCount} / {totalDays} Tage
          </Typography>
        </Box>
      )}

      {/* Instruction */}
      {!targetDate && !isRunning && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="time-lapse-instruction">
          Klicken Sie auf einen zukünftigen Tag, um das Zieldatum auszuwählen.
        </Typography>
      )}

      {/* Calendar Grid — 4 months at 1/4 width each */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {months.map(({ year, month }) => {
          const { startDow, totalDays: daysInMonth } = getMonthDays(year, month)
          return (
            <Paper
              key={`${year}-${month}`}
              elevation={0}
              sx={{ border: '1px solid', borderColor: 'divider', p: 1.5, flex: '1 1 0', minWidth: 0 }}
            >
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                {MONTH_NAMES[month]} {year}
              </Typography>
              {/* Weekday headers */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', mb: '2px' }}>
                {WEEKDAY_LABELS.map(label => (
                  <Typography
                    key={label}
                    variant="caption"
                    sx={{ textAlign: 'center', fontSize: '0.6rem', color: 'text.secondary' }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
              {/* Day cells */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                {Array.from({ length: startDow }).map((_, i) => (
                  <Box key={`empty-${i}`} sx={{ width: 24, height: 24 }} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1
                  const dateStr = formatDate(year, month, day)
                  const isProcessed = processedDates.has(dateStr)
                  const isCurrent = dateStr === currentDate
                  const isTarget = dateStr === targetDate
                  const isInRange = targetDate && dateStr > currentDate && dateStr < targetDate
                  const isClickable = !isRunning && !reachedTarget && dateStr > startDate

                  let bgColor = 'grey.100'
                  if (isInRange) bgColor = 'grey.400'
                  if (isProcessed) bgColor = 'success.light'
                  if (isCurrent) bgColor = 'warning.main'
                  if (isTarget) bgColor = 'primary.main'

                  const dayEvents = events.filter(e => e.date === dateStr)

                  return (
                    <Tooltip
                      key={day}
                      title={dayEvents.length > 0 ? dayEvents.map(e => e.description).join(', ') : dateStr}
                      arrow
                      placement="top"
                    >
                      <Box
                        data-testid={`day-${dateStr}`}
                        onClick={() => isClickable && handleDayClick(dateStr)}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '4px',
                          bgcolor: bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.6rem',
                          fontWeight: isCurrent || isTarget ? 700 : 400,
                          color: (isCurrent || isTarget) ? 'white' : 'text.primary',
                          transition: 'background-color 0.3s ease',
                          cursor: isClickable ? 'pointer' : 'default',
                          border: dayEvents.length > 0 ? '2px solid' : 'none',
                          borderColor: dayEvents.length > 0 ? 'primary.main' : 'transparent',
                          '&:hover': isClickable ? { bgcolor: 'primary.light', color: 'white' } : {},
                        }}
                      >
                        {day}
                      </Box>
                    </Tooltip>
                  )
                })}
              </Box>
            </Paper>
          )
        })}
      </Box>

      {/* Events log */}
      {events.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Ereignisse</Typography>
          <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
            {events.map((event, i) => (
              <Typography key={i} variant="caption" display="block" color={event.type === 'error' ? 'error' : 'text.secondary'}>
                {event.date}: {event.description}
                {event.amount ? <> (<FormattedCurrency value={event.amount} align="left" />)</> : ''}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}
